// components/shared-components/MaintenanceScreen
//
// Full-screen "We'll be right back" overlay. It is driven entirely by the
// `maintenance` redux slice, which the axios response interceptor toggles when
// the API returns a 503 carrying the maintenance marker. While it is showing it
// polls the (exempt) admin maintenance endpoint every ~30s and auto-recovers as
// soon as maintenance is turned off again.
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setMaintenance,
  clearMaintenance,
} from "store/slices/maintenanceSlice";
import MaintenanceService from "services/MaintenanceService";

const POLL_INTERVAL_MS = 30000;

const DEFAULT_MESSAGE =
  "We're performing some scheduled maintenance right now. We'll be back shortly — thanks for your patience.";

const WrenchGearIcon = () => (
  <svg
    width="72"
    height="72"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Gear */}
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1.5v2M12 20.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1.5 12h2M20.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
  </svg>
);

const MaintenanceScreen = () => {
  const dispatch = useDispatch();
  const { active, message } = useSelector((state) => state.maintenance);

  useEffect(() => {
    if (!active) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await MaintenanceService.getState();
        if (cancelled) return;
        // The service returns the response body directly; be defensive about
        // shape in case of a wrapper/encryption layer.
        const state = res?.data && "enabled" in res.data ? res.data : res;
        const enabled = state?.enabled;
        if (enabled === false) {
          dispatch(clearMaintenance());
        } else if (enabled === true) {
          dispatch(setMaintenance(state?.message || ""));
        }
      } catch (e) {
        // Still unreachable / not authorized — keep showing and retry next tick.
      }
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [active, dispatch]);

  if (!active) return null;

  return (
    <div
      role="alertdialog"
      aria-live="polite"
      aria-label="Service under maintenance"
      className="fixed inset-0 z-[10050] flex items-center justify-center px-6"
      style={{
        background:
          "linear-gradient(160deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
      }}
    >
      <div className="w-full max-w-lg text-center text-white">
        <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/15">
          <WrenchGearIcon />
        </div>

        <h1 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
          We&apos;ll be right back
        </h1>

        <p className="mx-auto mb-10 max-w-md text-base leading-relaxed text-slate-300">
          {message && message.trim().length > 0 ? message : DEFAULT_MESSAGE}
        </p>

        <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75"></span>
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-sky-400"></span>
          </span>
          <span>Retrying automatically&hellip;</span>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceScreen;
